import { theme } from '@healingpaper/cell'
import { type ReactNode, useCallback, useEffect, useRef } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { createRoot } from 'react-dom/client'

import Confirm from './Confirm'
import Modal from './Modal'

import { Content } from '@/components'

export interface PageModal {
  open: () => void
  close: () => void
}

interface UseModalProps {
  onClose?: () => void
  onBackgroundClick?: () => void
  options?: {
    backgroundStyle?: React.CSSProperties
    disableCloseOnClickBackground?: boolean
  }
}

export function useModal(props?: UseModalProps): {
  createPageModal: (name: string, component: ReactNode, title?: string) => PageModal
  confirm: (title: string, content: string, okText?: string, cancelText?: string) => Promise<boolean>
  close: () => void
  open: (component: ReactNode) => void
} {
  const el = useRef<{ container: HTMLDivElement | null }>({ container: null })

  const openModal = useCallback(async (component: ReactNode, close: () => void): Promise<boolean> => {
    document.body.style.overflow = 'hidden'
    const modalContainer = document.querySelector('#modalContainer')

    if (modalContainer) {
      // 중복 생성을 위해 toastContainer 생성
      el.current.container = document.createElement('div')
      modalContainer.appendChild(el.current.container)
      const root = createRoot(el.current.container)
      root.render(
        <ThemeProvider theme={theme}>
          <ModalBackground
            id="ModalBackground"
            onClick={(e) => {
              if (
                (e.nativeEvent.target as HTMLElement)?.id === 'ModalBackground' &&
                !props?.options?.disableCloseOnClickBackground
              ) {
                close()
                props?.onBackgroundClick?.()
              }
            }}
            style={props?.options?.backgroundStyle}
          >
            {component}
          </ModalBackground>
        </ThemeProvider>,
      )
    }

    return modalContainer !== null
  }, [])

  const closeModal = useCallback(() => {
    document.body.style.overflow = 'auto'
    if (el.current.container) {
      el.current.container.remove()
      el.current.container = null
    }
    props?.onClose?.()
  }, [el.current.container, props?.onClose])

  // 전체화면이 없고, 라우트 변화가 없는 모달 열기
  const openPopupModal = useCallback(async (component: ReactNode): Promise<boolean> => {
    return await openModal(component, closeModal)
  }, [])

  // 라우트 변화가 있는 모달 열기
  const openPageModal = useCallback(async (component: ReactNode, title = ''): Promise<boolean> => {
    return await openModal(
      <Content padding={0}>
        <Modal title={title} close={closePageModal}>
          {component}
        </Modal>
      </Content>,
      closePageModal,
    )
  }, [])

  const closePageModal = useCallback(() => {
    if (/modal/.test(location.search)) {
      window.history.back()
    }
    props?.onClose?.()
  }, [props?.onClose])

  useEffect(() => {
    window.addEventListener('popstate', closeModal)
    return () => {
      window.removeEventListener('popstate', closeModal)
    }
  }, [])

  return {
    createPageModal(name: string, component: ReactNode, title = '') {
      const hasModalNameInUrlParams = process.browser && new RegExp(name).test(location.search)
      if (hasModalNameInUrlParams) {
        openPageModal(component, title)
      }

      return {
        async open() {
          await openPageModal(component, title)
          const connector = location.search ? '&' : '?'
          window.history.pushState(
            '',
            '',
            window.location.pathname + window.location.search + connector + 'modal=' + name,
          )
        },
        close: closePageModal,
      }
    },
    async confirm(title, content, okText = 'ok', cancelText = 'cancel') {
      return new Promise<boolean>(async (resolve, reject) => {
        // container 안에 렌더된 컴포넌트가 없을 경우
        if (
          !(await openPopupModal(
            <Confirm
              title={title}
              content={content}
              okText={okText}
              cancelText={cancelText}
              onConfirm={(isOk) => {
                resolve(isOk)
                closeModal()
              }}
            />,
          ))
        ) {
          reject('컨펌모달이 열리지 않음')
        }
      })
    },
    close: closeModal,
    // 내부 컨텐츠를 완전히 Custom 하는 경우 사용
    open(component: ReactNode) {
      openModal(component, closeModal)
    },
  }
}

const ModalBackground = styled.div`
  position: fixed;
  // NotificationUIContainer(1000) 보다 낮아야 함
  z-index: 900;
  width: 100%;
  height: 100%;
  left: 0px;
  top: 0px;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  div[class^='Content'] {
    height: 100%;
  }
`
